// PDF export for the Revenue report.
//
// Extracted from Reports.tsx during the Maintainability Sprint 2026-04-19
// (Task 1.2 squeeze). Kept in a dedicated module because `@react-pdf/renderer`
// is heavy and is imported dynamically so it only ships to users who actually
// click "Export PDF".
import { toast } from "sonner";
import type { RevenuePeriod, RevenueResponse } from "@/lib/admin-api/finance";
import { downloadBlob } from "./csv";

const PERIOD_LABELS: Record<RevenuePeriod, string> = {
  day: "Jour",
  week: "Semaine",
  month: "Mois",
  quarter: "Trimestre",
  year: "Annee",
};

/**
 * Generate a PDF of the current revenue data using @react-pdf/renderer.
 * The library is dynamically imported so it only ships to users who click
 * the Export PDF button (keeps the main bundle lean).
 */
export async function exportRevenuePdf(
  data: RevenueResponse | undefined,
  period: RevenuePeriod,
): Promise<void> {
  if (!data || data.series.length === 0) {
    toast.error("Aucune donnee a exporter pour cette periode.");
    return;
  }

  try {
    const { pdf, Document, Page, Text, View, StyleSheet } = await import(
      "@react-pdf/renderer"
    );

    const styles = StyleSheet.create({
      page: { padding: 40, fontSize: 10, fontFamily: "Helvetica" },
      title: { fontSize: 18, marginBottom: 6, color: "#1c1917" },
      subtitle: { fontSize: 10, marginBottom: 18, color: "#78716c" },
      kpiRow: { flexDirection: "row", marginBottom: 18, gap: 10 },
      kpi: {
        flex: 1,
        padding: 10,
        backgroundColor: "#fffbeb",
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#fde68a",
      },
      kpiLabel: { fontSize: 8, color: "#78716c", marginBottom: 2 },
      kpiValue: { fontSize: 12, color: "#1c1917" },
      tableHeader: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderColor: "#d6d3d1",
        paddingBottom: 4,
        marginBottom: 4,
      },
      tableRow: {
        flexDirection: "row",
        paddingVertical: 3,
        borderBottomWidth: 0.5,
        borderColor: "#f5f5f4",
      },
      cellDate: { width: "28%", fontSize: 9 },
      cellRight: { width: "24%", fontSize: 9, textAlign: "right" },
      headerDate: { width: "28%", fontSize: 9, color: "#78716c" },
      headerRight: {
        width: "24%",
        fontSize: 9,
        color: "#78716c",
        textAlign: "right",
      },
    });

    const now = new Date().toLocaleDateString("fr-SN");
    const fmt = (n: number) =>
      `${new Intl.NumberFormat("fr-SN").format(n)} FCFA`;

    const doc = (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.title}>
            Rapport Revenu — {PERIOD_LABELS[period]}
          </Text>
          <Text style={styles.subtitle}>
            Genere le {now} · Djayma · CIEDA
          </Text>

          <View style={styles.kpiRow}>
            <View style={styles.kpi}>
              <Text style={styles.kpiLabel}>Revenu total</Text>
              <Text style={styles.kpiValue}>{fmt(data.kpis.total_fcfa)}</Text>
            </View>
            <View style={styles.kpi}>
              <Text style={styles.kpiLabel}>Commandes</Text>
              <Text style={styles.kpiValue}>
                {data.kpis.orders_count.toString()}
              </Text>
            </View>
            <View style={styles.kpi}>
              <Text style={styles.kpiLabel}>Panier moyen</Text>
              <Text style={styles.kpiValue}>
                {fmt(data.kpis.avg_order_fcfa)}
              </Text>
            </View>
          </View>

          <View style={styles.tableHeader}>
            <Text style={styles.headerDate}>Date</Text>
            <Text style={styles.headerRight}>Revenu</Text>
            <Text style={styles.headerRight}>Commandes</Text>
            <Text style={styles.headerRight}>Panier moyen</Text>
          </View>
          {data.series.map((p) => (
            <View key={p.date} style={styles.tableRow}>
              <Text style={styles.cellDate}>{p.date}</Text>
              <Text style={styles.cellRight}>{fmt(p.revenue_fcfa)}</Text>
              <Text style={styles.cellRight}>{p.orders.toString()}</Text>
              <Text style={styles.cellRight}>{fmt(p.avg_order_fcfa)}</Text>
            </View>
          ))}
        </Page>
      </Document>
    );

    const blob = await pdf(doc).toBlob();
    const date = new Date().toISOString().split("T")[0];
    downloadBlob(blob, `rapport-revenu-${period}-${date}.pdf`);
    toast.success("Export PDF telecharge.");
  } catch (err) {
    toast.error(
      err instanceof Error
        ? `Export PDF echoue: ${err.message}`
        : "Export PDF echoue.",
    );
  }
}
